import { useState } from 'react'
import {
  Users,
  User,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { lawyerCasesAPI } from '@/utils/api'
import { formatDate } from './utils'
import type { Passenger } from './types'

interface PassengersCardProps {
  caseId: number
  passengers: Passenger[]
  onUpdate: () => void
}

export function PassengersCard({ caseId, passengers, onUpdate }: PassengersCardProps) {
  // Editing state
  const [editingPassengerId, setEditingPassengerId] = useState<number | null>(null)
  const [editPassengerData, setEditPassengerData] = useState<Partial<Passenger>>({})
  const [savingPassengerId, setSavingPassengerId] = useState<number | null>(null)
  const [deletingPassengerId, setDeletingPassengerId] = useState<number | null>(null)

  // Add state
  const [showAddPassenger, setShowAddPassenger] = useState(false)
  const [newPassenger, setNewPassenger] = useState({ first_name: '', last_name: '', birthdate: '' })
  const [addingPassenger, setAddingPassenger] = useState(false)

  const startEditPassenger = (passenger: Passenger) => {
    setEditingPassengerId(passenger.id)
    setEditPassengerData({
      first_name: passenger.first_name,
      last_name: passenger.last_name,
      birthdate: passenger.birthdate || '',
    })
  }

  const cancelEditPassenger = () => {
    setEditingPassengerId(null)
    setEditPassengerData({})
  }

  const savePassenger = async (passengerId: number) => {
    setSavingPassengerId(passengerId)
    try {
      await lawyerCasesAPI.updatePassenger(caseId, passengerId, editPassengerData)
      toast.success('Passagier aktualisiert')
      setEditingPassengerId(null)
      setEditPassengerData({})
      onUpdate()
    } catch (err) {
      console.error('Failed to update passenger:', err)
      toast.error('Fehler beim Speichern')
    } finally {
      setSavingPassengerId(null)
    }
  }

  const deletePassenger = async (passengerId: number) => {
    setDeletingPassengerId(passengerId)
    try {
      await lawyerCasesAPI.deletePassenger(caseId, passengerId)
      toast.success('Passagier gelöscht')
      onUpdate()
    } catch (err) {
      console.error('Failed to delete passenger:', err)
      toast.error('Fehler beim Löschen')
    } finally {
      setDeletingPassengerId(null)
    }
  }

  const addPassenger = async () => {
    if (!newPassenger.first_name || !newPassenger.last_name) {
      toast.error('Vor- und Nachname erforderlich')
      return
    }
    setAddingPassenger(true)
    try {
      await lawyerCasesAPI.createPassenger(caseId, {
        first_name: newPassenger.first_name,
        last_name: newPassenger.last_name,
        birthdate: newPassenger.birthdate || null,
      })
      toast.success('Passagier hinzugefügt')
      setShowAddPassenger(false)
      setNewPassenger({ first_name: '', last_name: '', birthdate: '' })
      onUpdate()
    } catch (err) {
      console.error('Failed to add passenger:', err)
      toast.error('Fehler beim Hinzufügen')
    } finally {
      setAddingPassenger(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Users className="h-5 w-5 text-slate-400" />
          Passagiere
          <Badge variant="secondary">{passengers.length}</Badge>
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddPassenger(true)}
          className="gap-1"
        >
          <Plus className="h-3.5 w-3.5" />
          Hinzufügen
        </Button>
      </div>

      {/* Add Passenger Form */}
      {showAddPassenger && (
        <div className="mb-4 p-3 bg-highlight-hover rounded-lg border border-highlight">
          <div className="grid grid-cols-3 gap-2 mb-2">
            <Input
              placeholder="Vorname"
              value={newPassenger.first_name}
              onChange={(e) => setNewPassenger({ ...newPassenger, first_name: e.target.value })}
            />
            <Input
              placeholder="Nachname"
              value={newPassenger.last_name}
              onChange={(e) => setNewPassenger({ ...newPassenger, last_name: e.target.value })}
            />
            <Input
              type="date"
              placeholder="Geburtsdatum"
              value={newPassenger.birthdate}
              onChange={(e) => setNewPassenger({ ...newPassenger, birthdate: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddPassenger(false)
                setNewPassenger({ first_name: '', last_name: '', birthdate: '' })
              }}
            >
              Abbrechen
            </Button>
            <Button size="sm" onClick={addPassenger} disabled={addingPassenger}>
              {addingPassenger ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
            </Button>
          </div>
        </div>
      )}

      {passengers.length === 0 ? (
        <p className="text-slate-500 text-sm">Noch keine Passagiere hinzugefügt.</p>
      ) : (
        <div className="space-y-2">
          {passengers.map((passenger) => (
            <div key={passenger.id} className="p-3 bg-slate-50 rounded-lg">
              {editingPassengerId === passenger.id ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Vorname"
                      value={editPassengerData.first_name || ''}
                      onChange={(e) =>
                        setEditPassengerData({ ...editPassengerData, first_name: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Nachname"
                      value={editPassengerData.last_name || ''}
                      onChange={(e) =>
                        setEditPassengerData({ ...editPassengerData, last_name: e.target.value })
                      }
                    />
                    <Input
                      type="date"
                      value={editPassengerData.birthdate || ''}
                      onChange={(e) =>
                        setEditPassengerData({ ...editPassengerData, birthdate: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={cancelEditPassenger}>
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => savePassenger(passenger.id)}
                      disabled={savingPassengerId === passenger.id}
                    >
                      {savingPassengerId === passenger.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-slate-400" />
                    <span className="font-medium text-slate-900">
                      {passenger.first_name} {passenger.last_name}
                    </span>
                    <span className="text-sm text-slate-500">
                      {passenger.birthdate ? formatDate(passenger.birthdate) : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => startEditPassenger(passenger)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deletePassenger(passenger.id)}
                      disabled={deletingPassengerId === passenger.id}
                    >
                      {deletingPassengerId === passenger.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
